import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { UserService } from 'src/app/shared/services/user-service';
import { BaseUserModel } from 'src/app/Models/userModel';
import { CustuomtoastserviceService } from 'src/app/shared/services/custuomtoastservice.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { catchError, forkJoin, of, switchMap, throwError } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import {
  IMSProductBaseRequest,
  ProductImageModel,
  ProductInternalStatus,
  ProductListingStatus,
  ProductSupplyType,
} from '../../../models/product.model';
import { ProductService } from '../../../services/product-api.service';
import { NamedResponse } from 'src/app/shared/models/shared.model';

@Component({
  selector: 'app-ai-bot',
  standalone: true,
  imports: [CommonModule, MatIconModule, FormsModule],
  templateUrl: './ai-bot.component.html',
  styleUrl: './ai-bot.component.css',
})
export class AiBotComponent {
  @Output() closeDialog = new EventEmitter<void>();
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;
  @Output() notify = new EventEmitter<void>();

  @ViewChild('scrollMe') private scrollContainer!: ElementRef;

  currentStep: number = 1;
  productNameInput: string = '';
  productDescriptionInput: string = '';
  productName: string = '';
  selectedFiles: File[] = [];
  imagePreviewUrl: string[] = [];
  showImagePopup: boolean = false;
  isLoading: boolean = false;
  showThankYouPopup: boolean = false;
  showAddNewProductPopup: boolean = false;
  userDetails: BaseUserModel = new BaseUserModel();
  showCameraPopup: boolean = false;
  capturedImage: string | null = null;
  videoStream: MediaStream | null = null;
  showNotification: boolean = false;
  notificationType: 'success' | 'error' | 'warning' = 'success';
  notificationTitle: string = '';
  notificationMessage: string = '';
  notificationIcon: string = '';
  warehouseId: number = 0;
  productStatus: number = 2;

  createProductRequest: IMSProductBaseRequest = new IMSProductBaseRequest();
  addedProducts: {
    name: string;
    description: string;
    condition: string;
    images: string[];
  }[] = [];
  showWelcomeMessage: boolean = true;
  constructor(
    private userService: UserService,
    private toaster: CustuomtoastserviceService,
    private errorService: ErrorHandlerService,
    private productService: ProductService
  ) {}

  async ngOnInit() {
    this.warehouseId = Number(localStorage.getItem('warehouseId'));
    const user = await this.userService.getUserFromLocalStorage();
    if (user) {
      this.userDetails = user;
    } else {
      // Handle the case where user is null
      this.toaster.error('User details not found');
    }
    this.loadLookups();
  } // notification popup
  showNotificationPopup(
    type: 'success' | 'error' | 'warning',
    title: string,
    message: string,
    icon: string
  ): void {
    this.notificationType = type;
    this.notificationTitle = title;
    this.notificationMessage = message;
    this.notificationIcon = icon;
    this.showNotification = true;

    // Auto-close
    if (type !== 'error') {
      setTimeout(() => {
        this.closeNotification();
      }, 3000);
    }
  }

  //  close notification popup
  closeNotification(): void {
    this.showNotification = false;
  }
  showThankYou() {
    this.showAddNewProductPopup = false;
    this.showThankYouPopup = true;
  }

  // camera capture
  openCamera(): void {
    this.showCameraPopup = true;
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        this.videoStream = stream;
        const videoTracks = stream.getVideoTracks();
        if (videoTracks.length === 0 || !videoTracks[0].enabled) {
          this.showNotificationPopup(
            'error',
            'Camera Error',
            'Please turn on your camera to capture images.',
            'error'
          );
          this.closeCamera();
          return;
        }
        this.videoElement.nativeElement.srcObject = stream;
      })
      .catch((error) => {
        let errorMessage = 'An error occurred while accessing the camera.';
        if (error.name === 'NotAllowedError') {
          errorMessage =
            'Camera access denied. Please allow camera permissions.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No camera found. Please connect a camera.';
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'Camera is already in use by another application.';
        }
        this.showNotificationPopup(
          'error',
          'Camera Error',
          errorMessage,
          'error'
        );
        this.showCameraPopup = false;
      });
  }

  // A camera capture
  closeCamera(): void {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach((track) => track.stop());
      this.videoStream = null;
    }
    this.capturedImage = null;
    this.showCameraPopup = false;
  }

  // Capture image from video
  captureImage(): void {
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context && video.videoWidth > 0 && video.videoHeight > 0) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      this.capturedImage = canvas.toDataURL('image/jpeg');
    } else {
      this.showNotificationPopup(
        'error',
        'Capture Error',
        'Failed to capture image. Please try again.',
        'error'
      );
    }
  }

  //  Retake image
  retakeImage(): void {
    this.capturedImage = null;
  }
  //  Add captured image to list without closing popup
  addCapturedImage(): void {
    if (this.capturedImage) {
      // Convert data URL to File object
      fetch(this.capturedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], `captured-image-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          this.selectedFiles.push(file);
          this.imagePreviewUrl.push(this.capturedImage!);
          this.showNotificationPopup(
            'success',
            'Image Captured',
            'Image successfully captured and added.',
            'check_circle'
          );
          this.capturedImage = null;
        })

        .catch(() => {
          // CHANGED: Handle error in converting captured image
          this.showNotificationPopup(
            'error',
            'Image Error',
            'Failed to process captured image.',
            'error'
          );
        });
    }
  }

  // Added for camera capture: Use captured image
  useCapturedImage(): void {
    if (this.capturedImage) {
      // Convert data URL to File object
      fetch(this.capturedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], `captured-image-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          this.selectedFiles.push(file);
          this.imagePreviewUrl.push(this.capturedImage!);
          // this.closeCamera();
        });
    }
  }

// Common file validation and preview method
private validateAndAddFiles(files: File[]): void {
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
  ];
  const maxSizeMB = 5;

  files.forEach((file) => {
    // type validation
    if (!allowedTypes.includes(file.type)) {
      this.toaster.error(`Invalid file type: ${file.name} 'Upload Failed'`);
      return;
    }

    // size validation
    if (file.size > maxSizeMB * 1024 * 1024) {
      this.toaster.error(
        `${file.name} exceeds ${maxSizeMB}MB, 'Upload Skipped'`
      );
      return;
    }

    // add valid file
    this.selectedFiles.push(file);

    // preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        this.imagePreviewUrl.push(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  });
}


// Handles file input upload
onImageUpload(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    const newFiles = Array.from(input.files);
    this.validateAndAddFiles(newFiles);
    input.value = ''; // reset file input
  }
}


// Handles drag and drop upload
handleDrop(event: DragEvent): void {
  event.preventDefault();
  if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
    const newFiles = Array.from(event.dataTransfer.files);
    this.validateAndAddFiles(newFiles);
  }
}


// Optional: to prevent browser from opening dropped images
handleDragOver(event: DragEvent): void {
  event.preventDefault();
}

  handleDragLeave(event: DragEvent): void {
    event.preventDefault();
  }

  ngAfterViewChecked() {
    this.scrollContainer.nativeElement.scrollTop =
      this.scrollContainer.nativeElement.scrollHeight;
  }
  onYesClick() {
    this.currentStep = 1;
  }

  onNoClick() {
    this.closeDialog.emit();
    // this.showThankYouPopup =true
  }

  submitProductName() {
    if (this.productNameInput.trim()) {
      this.productName = this.productNameInput.trim();
      this.currentStep = 2;
    } else {
      this.showNotificationPopup(
        'warning',
        'Input Required',
        'Please enter a product name.',
        'warning'
      );
    }
  }

  submitProductDescription() {
    if (this.productDescriptionInput.trim()) {
      this.createProductRequest.description =
        this.productDescriptionInput.trim();
      this.currentStep = 3; // Move to image upload
    } else {
      this.showNotificationPopup(
        'warning',
        'Input Required',
        'Please enter a product description.',
        'warning'
      );
    }
  }
  openImagePopup() {
    this.showCameraPopup = false;
    this.showImagePopup = true;
  }

  closeImagePopup() {
    this.showImagePopup = false;
    this.selectedFiles = [];
    this.imagePreviewUrl = [];
  }
  removeImage(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.imagePreviewUrl.splice(index, 1);
  }

  submitImages() {
    if (this.imagePreviewUrl.length === 0) {
      this.showNotificationPopup(
        'warning',
        'No Images',
        'Please upload at least one image.',
        'warning'
      );
      return;
    }
    this.showImagePopup = false;
    this.currentStep = 3;
    this.showCameraPopup = false;
    // this.retrieveProductDetails();
    this.createProduct();
  }
  createProduct() {
    const warehouseLocationId = localStorage.getItem('warehouseLocationId');
    this.createProductRequest.listerProduct.listingStatus = ProductListingStatus.NotListed;
    this.isLoading = true;
    this.createProductRequest.name = this.productNameInput;
    this.createProductRequest.description = this.productDescriptionInput;
    this.createProductRequest.internalStatus =
      ProductInternalStatus.AIProcessing;
    this.createProductRequest.companyId = Number(this.userDetails.companyId);
    this.createProductRequest.warehouseId = this.warehouseId;

    this.createProductRequest.warehouseLocationId = warehouseLocationId
      ? Number(warehouseLocationId)
      : null;
    this.createProductRequest.listerProduct.supplyType =
      ProductSupplyType.DonatedGoods;
    this.productService
      .createProduct(this.createProductRequest)
      .pipe(
        switchMap((product) => {
          this.toaster.success('Product created successfully');
          this.showNotificationPopup(
            'success',
            'Success',
            'Product created successfully',
            'check_circle'
          );
          this.notify.emit();
          // this.showThankYouPopup = true;

          this.addedProducts.push({
            name: this.productName,
            description: this.productDescriptionInput,
            condition: this.productConditionsList.find(
              (condition) =>
                condition.id ===
                this.createProductRequest.listerProduct.condition
            )!.name,
            images: [...this.imagePreviewUrl],
          });

          if (!this.selectedFiles.length) {
            return of([]);
          }

          const uploadTasks = this.selectedFiles.map((file, index) =>
            this.productService
              .uploadImage(this.userDetails.companyId, product.id, file)
              .pipe(
                switchMap((uploadRes) => {
                  const imageRequest: ProductImageModel = {
                    id: 0,
                    blobFileId: uploadRes.blobFileId,
                    isPrimary: index === 0, // Only first image is primary
                    productId: product.id,
                    url: uploadRes.url,
                    imgFile: null,
                  };
                  return this.productService.mapProductImages(
                    product.id,
                    imageRequest
                  );
                }),
                catchError((err) => {
                  console.error('Image upload failed:', err);
                  return of(null);
                })
              )
          );

          return forkJoin(uploadTasks);
        }),
        catchError((error) => {
          this.handleProductCreationError(error);
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (responses) => {
          const successful = responses.filter((r) => r !== null).length;
          if (successful) {
            this.showNotificationPopup(
              'success',
              'Success',
              `Product created with ${successful} images`,
              'check_circle'
            );

            this.toaster.success(`Product created with ${successful} images`);
          }
          this.showAddNewProductPopup = true;
          this.resetForm();
        },
        error: (error) => {
          console.error('Final subscription error:', error);
          this.showNotificationPopup(
            'error',
            'Product Creation Error',
            'Product creation failed.',
            'error'
          );
          this.isLoading = false;
          this.toaster.error('Product creation failed.');
        },
      });
  }
  private handleProductCreationError(error: any) {
    this.isLoading = false;
    this.currentStep = 1;
    this.productNameInput = '';
    this.productDescriptionInput = '';
    this.selectedFiles = [];
    this.imagePreviewUrl = [];
    this.addedProducts = [];
    this.showNotificationPopup(
      'error',
      'Product Creation Error',
      'Failed to save product to database.',
      'error'
    );
    this.errorService.handleError(error);
  }

  private resetForm() {
    this.isLoading = false;
    this.currentStep = 1;
    this.productNameInput = '';
    this.productDescriptionInput = '';
    this.selectedFiles = [];
    this.imagePreviewUrl = [];
    this.createProductRequest = new IMSProductBaseRequest();
    // this.showWelcomeMessage = false;
  }

  // Add new product popup methods
  closeAddNewProductPopup() {
    this.showAddNewProductPopup = false;
  }

  addAnotherProduct() {
    this.showAddNewProductPopup = false;
    this.currentStep = 1;
    this.productNameInput = '';
    this.productName = '';
    this.showWelcomeMessage = false;
  }

  closeBot() {
    this.showThankYouPopup = false;
    this.addedProducts = [];
    this.closeDialog.emit();
  }

  closeThankYouPopup() {
    this.showThankYouPopup = false;
    this.addedProducts = [];
    this.currentStep = 1;
    this.productDescriptionInput = '';
    this.productName = '';
    this.selectedFiles = [];
    this.imagePreviewUrl = [];
    this.isLoading = false;
    this.showWelcomeMessage = true;
    this.closeCamera();
  }
  productConditionsList: NamedResponse[] = [];

  async loadLookups(): Promise<void> {
    try {
      const storageKey = 'lookups';
      const storedData = localStorage.getItem(storageKey);
      if (!storedData) return;

      const dataArray = JSON.parse(storedData);
      if (!Array.isArray(dataArray)) return;

      const productConditions = dataArray.find(
        (item) => item.name === 'ProductCondition'
      );
      this.productConditionsList = productConditions?.items || [];
    } catch (error) {}
  }
  conditionSelected() {
    this.currentStep = 4;
  }
}
